/**
 * iOS-style home-screen badge ("3" red dot) helpers built on the
 * standardized App Badging API (`navigator.setAppBadge` / `clearAppBadge`).
 *
 * Supported today on:
 *   - iOS 16.4+ (PWAs added to the Home Screen)
 *   - macOS / iPadOS Safari PWAs
 *   - Chrome / Edge desktop PWAs
 *
 * Unsupported browsers are silently no-op'd so call sites don't need to
 * feature-detect.
 */

const STORAGE_KEY = 'dl_app_badge_count';

const isSupported = () =>
  typeof navigator !== 'undefined' && typeof navigator.setAppBadge === 'function';

/** Persist the badge count so we can restore it after a reload. */
const persist = (n) => {
  try { localStorage.setItem(STORAGE_KEY, String(n)); } catch (_) { /* ignore */ }
};

const restorePersisted = () => {
  try { return Number(localStorage.getItem(STORAGE_KEY)) || 0; } catch (_) { return 0; }
};

/** Set the home-screen badge to an exact count (0 = clear). */
export const setAppBadge = async (count) => {
  const n = Math.max(0, Math.floor(Number(count) || 0));
  persist(n);
  if (!isSupported()) return;
  try {
    if (n === 0) { await navigator.clearAppBadge(); }
    else { await navigator.setAppBadge(n); }
  } catch (_) { /* permission / unsupported context — ignore */ }
};

/** Clear the home-screen badge entirely. */
export const clearAppBadge = () => setAppBadge(0);

/** Increment the badge by `delta` (defaults 1). */
export const bumpAppBadge = async (delta = 1) => {
  const next = restorePersisted() + Math.max(1, Math.floor(delta));
  await setAppBadge(next);
};

/**
 * Wire automatic "user has seen it" clearing — when the tab becomes
 * visible again, drop the badge to zero. Caller should invoke this once
 * at app boot (App.js useEffect).
 */
export const installAppBadgeAutoClear = () => {
  if (typeof document === 'undefined') return () => {};
  const onVisible = () => {
    if (document.visibilityState === 'visible') {
      clearAppBadge();
    }
  };
  document.addEventListener('visibilitychange', onVisible);
  // Fire once on mount in case we're already foreground after a tap.
  onVisible();
  return () => document.removeEventListener('visibilitychange', onVisible);
};
